import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, RefreshCw, Edit2, Trash2, ArrowLeft, Users, Eye,
  Palette, Scissors, Sparkles, GraduationCap, Heart, ChefHat,
  Calendar, Crown, Settings, Image as ImageIcon, Save, X
} from "lucide-react";
import PresetConfiguratorModal from "@/components/PresetConfiguratorModal";

// Icon mapping for categories
const CATEGORY_ICONS = {
  palette: Palette,
  scissors: Scissors,
  sparkles: Sparkles,
  graduation: GraduationCap,
  heart: Heart,
  chef: ChefHat,
  calendar: Calendar,
  crown: Crown,
  default: Sparkles
};

// Predefined colors
const CATEGORY_COLORS = [
  { name: "Pink", value: "#EC4899" },
  { name: "Purple", value: "#8B5CF6" },
  { name: "Blue", value: "#3B82F6" },
  { name: "Cyan", value: "#06B6D4" },
  { name: "Teal", value: "#14B8A6" },
  { name: "Green", value: "#22C55E" },
  { name: "Orange", value: "#F59E0B" },
  { name: "Red", value: "#EF4444" },
];

export default function CategoryManagementPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    icon: "sparkles",
    color: "#8B5CF6",
    is_active: true,
    icon_url: ""
  });
  const [uploadingIcon, setUploadingIcon] = useState(false);

  // Upload icon to Cloudinary via the universal pilot-upload endpoint
  const handleIconUpload = async (file) => {
    if (!file) return;
    setUploadingIcon(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "category_icons");
      const res = await fetch(`${API}/pilot/upload-image`, {
        method: "POST",
        body: fd,
      });
      if (res.ok) {
        const data = await res.json();
        const url = data.url || data.secure_url;
        setFormData(prev => ({ ...prev, icon_url: url }));
        toast.success("Icon uploaded");
      } else {
        toast.error("Icon upload failed");
      }
    } catch (e) {
      toast.error("Icon upload failed");
    } finally {
      setUploadingIcon(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Flatten the nested structure - extract all sub_categories from all category types
        const allCategories = (data.categories || []).flatMap(cat => cat.sub_categories || []);
        setCategories(allCategories);
      }
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Generate slug from name
  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      description: "",
      icon: "sparkles",
      color: "#8B5CF6",
      is_active: true,
      icon_url: ""
    });
  };

  // Open add dialog - using new PresetConfiguratorModal
  const handleAddNew = () => {
    resetForm();
    setEditingCategory(null);
    setShowPresetModal(true);
  };

  // Open edit dialog - using new PresetConfiguratorModal
  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowPresetModal(true);
  };

  // Save from PresetConfiguratorModal
  const handlePresetSave = async (payload) => {
    const token = localStorage.getItem("session_token");
    const isEditing = !!editingCategory;
    
    const categoryPayload = {
      name: payload.name,
      slug: payload.slug || generateSlug(payload.name),
      description: payload.description,
      icon: payload.icon,
      color: payload.color,
      is_active: payload.is_active,
      branding: payload.branding,
    };
    
    try {
      if (isEditing) {
        const res = await fetch(`${API}/admin/portal-categories/${editingCategory.sub_category_id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(categoryPayload)
        });
        if (!res.ok) throw new Error("Failed to update");
      } else {
        const res = await fetch(`${API}/admin/portal-categories`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(categoryPayload)
        });
        if (!res.ok) throw new Error("Failed to create");
      }
      
      fetchCategories();
    } catch (err) {
      throw err;
    }
  };

  // Legacy save new category (for backward compatibility)
  const handleSaveNew = async () => {
    if (!formData.name.trim()) {
      toast.error("Category name is required");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const payload = {
        ...formData,
        slug: formData.slug || generateSlug(formData.name)
      };
      
      const res = await fetch(`${API}/admin/portal-categories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success("Category created!");
        setShowAddDialog(false);
        resetForm();
        fetchCategories();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to create category");
      }
    } catch (err) {
      toast.error("Error creating category");
    } finally {
      setSaving(false);
    }
  };

  // Update existing category
  const handleSaveEdit = async () => {
    if (!formData.name.trim() || !editingCategory) {
      toast.error("Category name is required");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const payload = {
        ...formData,
        slug: formData.slug || generateSlug(formData.name)
      };
      
      const res = await fetch(`${API}/admin/portal-categories/${editingCategory.sub_category_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success("Category updated!");
        setShowEditDialog(false);
        setEditingCategory(null);
        resetForm();
        fetchCategories();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update category");
      }
    } catch (err) {
      toast.error("Error updating category");
    } finally {
      setSaving(false);
    }
  };

  // Delete category
  const handleDelete = async (category) => {
    if (!window.confirm(`Delete "${category.name}"? This cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-categories/${category.sub_category_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Category deleted");
        fetchCategories();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to delete category");
      }
    } catch (err) {
      toast.error("Error deleting category");
    }
  };

  // Get icon component
  const getIconComponent = (iconName) => {
    return CATEGORY_ICONS[iconName] || CATEGORY_ICONS.default;
  };

  // Category Form Component (reused for both add and edit)
  const CategoryForm = ({ onSave, saveLabel }) => (
    <div className="space-y-4">
      {/* Name */}
      <div>
        <Label>Category Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => {
            const name = e.target.value;
            setFormData(prev => ({
              ...prev,
              name,
              slug: generateSlug(name)
            }));
          }}
          placeholder="e.g., Makeup Artists"
          data-testid="category-name-input"
        />
      </div>
      
      {/* Slug */}
      <div>
        <Label>URL Slug</Label>
        <Input
          value={formData.slug}
          onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
          placeholder="makeup-artists"
          className="text-gray-500"
        />
        <p className="text-xs text-gray-400 mt-1">Used in URLs: /pilots/{formData.slug || "slug"}</p>
      </div>
      
      {/* Description */}
      <div>
        <Label>Description</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Brief description of this category..."
          rows={2}
        />
      </div>
      
      {/* Icon Selection */}
      <div>
        <Label>Icon</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {Object.entries(CATEGORY_ICONS).filter(([k]) => k !== "default").map(([key, Icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, icon: key }))}
              className={`p-2 rounded-lg border-2 transition-colors ${
                formData.icon === key 
                  ? "border-purple-500 bg-purple-50" 
                  : "border-gray-200 hover:border-gray-300"
              }`}
              title={key}
            >
              <Icon className="w-5 h-5" style={{ color: formData.color }} />
            </button>
          ))}
        </div>
      </div>

      {/* Custom icon upload (overrides Lucide icon when set) */}
      <div>
        <Label className="flex items-center gap-1">
          <ImageIcon className="w-3.5 h-3.5" /> Custom Icon (PNG/SVG)
          <span className="text-[10px] text-gray-400 ml-1">overrides Lucide icon when uploaded</span>
        </Label>
        <div className="mt-2 flex items-center gap-3">
          {formData.icon_url ? (
            <div className="relative w-14 h-14 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center" data-testid="category-icon-preview">
              <img src={formData.icon_url} alt="Custom icon" className="max-w-full max-h-full object-contain" />
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, icon_url: "" }))}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                title="Remove custom icon"
                data-testid="category-icon-remove"
              ><X className="w-3 h-3" /></button>
            </div>
          ) : (
            <div className="w-14 h-14 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-300">
              <ImageIcon className="w-5 h-5" />
            </div>
          )}
          <label className="flex-1 cursor-pointer">
            <input
              type="file"
              accept="image/png,image/svg+xml,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => handleIconUpload(e.target.files?.[0])}
              data-testid="category-icon-upload-input"
            />
            <div className="px-3 py-2 rounded-lg border border-gray-200 hover:border-purple-400 hover:bg-purple-50 text-xs text-gray-600 text-center transition-colors">
              {uploadingIcon ? "Uploading..." : (formData.icon_url ? "Replace" : "Upload to Cloudinary")}
            </div>
          </label>
        </div>
      </div>
      
      {/* Color Selection */}
      <div>
        <Label>Color</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {CATEGORY_COLORS.map(({ name, value }) => (
            <button
              key={value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, color: value }))}
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                formData.color === value 
                  ? "border-gray-800 scale-110" 
                  : "border-transparent hover:scale-105"
              }`}
              style={{ backgroundColor: value }}
              title={name}
            />
          ))}
          {/* Custom color picker */}
          <div className="relative">
            <input
              type="color"
              value={formData.color}
              onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
              className="w-8 h-8 rounded-full cursor-pointer border-2 border-dashed border-gray-300"
            />
          </div>
        </div>
      </div>
      
      {/* Active Toggle */}
      <div className="flex items-center justify-between py-2">
        <div>
          <Label>Active</Label>
          <p className="text-xs text-gray-500">Show this category to users</p>
        </div>
        <Switch
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
        />
      </div>
      
      {/* Preview */}
      <div className="border-t pt-4">
        <Label className="text-xs text-gray-500 mb-2 block">Preview</Label>
        <div 
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg"
          style={{ backgroundColor: `${formData.color}15` }}
        >
          {(() => {
            const Icon = getIconComponent(formData.icon);
            return <Icon className="w-5 h-5" style={{ color: formData.color }} />;
          })()}
          <span className="font-medium" style={{ color: formData.color }}>
            {formData.name || "Category Name"}
          </span>
        </div>
      </div>
      
      <DialogFooter>
        <Button variant="outline" onClick={() => { setShowAddDialog(false); setShowEditDialog(false); }}>Cancel</Button>
        <Button onClick={onSave} disabled={saving} data-testid="save-category-btn">
          {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />{saveLabel}</>}
        </Button>
      </DialogFooter>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      {/* Header */}
      <div className="max-w-5xl mx-auto mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/admin/pilots")}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Pilots
        </Button>
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Pilot Categories</h1>
            <p className="text-gray-600">Manage category templates for pilot programs</p>
          </div>
          <Button onClick={handleAddNew} data-testid="add-category-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        </div>
      </div>
      
      {/* Categories Grid */}
      <div className="max-w-5xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
          </div>
        ) : categories.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Sparkles className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">No categories yet. Create your first one!</p>
              <Button onClick={handleAddNew}>
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat) => {
              const Icon = getIconComponent(cat.icon);
              return (
                <Card 
                  key={cat.sub_category_id} 
                  className="hover:shadow-md transition-shadow"
                  data-testid={`category-card-${cat.slug}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${cat.color || "#8B5CF6"}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: cat.color || "#8B5CF6" }} />
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleEdit(cat)}
                          data-testid={`edit-category-${cat.slug}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(cat)}
                          data-testid={`delete-category-${cat.slug}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <h3 className="font-semibold text-lg mb-1">{cat.name}</h3>
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                      {cat.description || "No description"}
                    </p>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1 text-gray-400">
                        <Users className="w-3.5 h-3.5" />
                        <span>{cat.member_count || 0} members</span>
                      </div>
                      <select
                        value={cat.launch_status || (cat.is_active !== false ? "pilot" : "hidden")}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          try {
                            const token = localStorage.getItem("session_token");
                            const res = await fetch(`${API}/admin/portal-categories/${cat.sub_category_id}`, {
                              method: "PUT",
                              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                              body: JSON.stringify({ launch_status: newStatus, is_active: newStatus !== "hidden" })
                            });
                            if (res.ok) { toast.success(`${cat.name} set to ${newStatus}`); fetchCategories(); }
                          } catch { toast.error("Failed to update status"); }
                        }}
                        className={`text-xs font-medium px-2 py-1 rounded-lg border cursor-pointer ${
                          (cat.launch_status || "pilot") === "live" ? "bg-green-50 border-green-200 text-green-700" :
                          (cat.launch_status || "pilot") === "pilot" ? "bg-amber-50 border-amber-200 text-amber-700" :
                          (cat.launch_status || "pilot") === "coming_soon" ? "bg-blue-50 border-blue-200 text-blue-700" :
                          "bg-gray-100 border-gray-200 text-gray-500"
                        }`}
                        data-testid={`status-select-${cat.slug}`}
                      >
                        <option value="live">Live</option>
                        <option value="pilot">Pilot</option>
                        <option value="coming_soon">Coming Soon</option>
                        <option value="hidden">Hidden</option>
                      </select>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Add Category Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
            <DialogDescription>Create a new pilot category template</DialogDescription>
          </DialogHeader>
          <CategoryForm onSave={handleSaveNew} saveLabel="Create Category" />
        </DialogContent>
      </Dialog>
      
      {/* Edit Category Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => { setShowEditDialog(open); if (!open) setEditingCategory(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
            <DialogDescription>Update category settings</DialogDescription>
          </DialogHeader>
          <CategoryForm onSave={handleSaveEdit} saveLabel="Save Changes" />
        </DialogContent>
      </Dialog>
      
      {/* New Preset Configurator Modal */}
      <PresetConfiguratorModal
        isOpen={showPresetModal}
        onClose={() => { setShowPresetModal(false); setEditingCategory(null); }}
        category={editingCategory}
        isEdit={!!editingCategory}
        onSave={handlePresetSave}
      />
    </div>
  );
}
